import React, { useContext, useState, useEffect } from 'react';
import { View, TextInput, Pressable, Switch } from 'react-native';
import { useMutation } from '@apollo/client';
import webStyled from 'styled-components';
import styled from 'styled-components/native';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEnvelope,
  faMessageLines,
  faNote,
  faPhone
} from '@fortawesome/pro-regular-svg-icons';

import colors from '../../../styles/colors';
import { RECORD_USER_ACTIVITY } from '../../../hooks/useSubscriptions/queries';
import MessageInputContext from '../../../contexts/MessageInputContext';

import { CREATE_MESSAGE } from './queries';

const Input = styled(TextInput)`
  background-color: white;
  outline-width: 0px;
  outline-color: white;
  width: 100%;
`;

const ButtonContainer = webStyled.div`
  background-color: white;
  align-items: center;
  justify-content: flex-end;
  column-gap: 20px;
  flex-direction: row;
`;

const Container = webStyled.div`
  margin: 10px;
`;

const SendButton = webStyled.div`
  background-color: ${colors.elRed};
  margin: 10px;
  align-items: center;
  border-radius: 20px;
  padding-left: 12px;
  padding-right: 12px;
  padding-top: 8px;
  padding-bottom: 8px;
  shadow-offset: 2px 2px;
  shadow-opacity: 0.3;
  shadow-radius: 8px;
  float:right;
`;

const SendLabel = webStyled.div`
  color: white;
  font-size: 18px;
  font-weight: bold;
`;

const Tabs = webStyled.div`
  flex-direction: row;
  height: ${props => props.minimal ? '50px' : '80px'};
`;

const ACTIVE_BORDER_BOTTOM = `
  border-bottom-style: solid;
  border-bottom-width: 4px;
  border-bottom-color: ${colors.highlight};
`;

const Tab = webStyled.div`
  flex-direction: row;
  flex-grow: 1;
  align-items: center;
  border-style: solid;
  border-width: 1px;
  border-color: ${colors.border};
  justify-content: center;
  column-gap: 10px;
  background-color: ${props => props.active ? 'white' : colors.tabs};
  ${props => props.active ? ACTIVE_BORDER_BOTTOM : ''}
`;

const TabLabel = webStyled.div`
  color: ${props => (
    props.enabled ?
      (props.active ? colors.highlight : colors.darkText) :
      colors.lightText
  )};
  font-size: 20px;
`;

const Form = webStyled.div`
  border-style: solid;
  border-width: 1px;
  border-color: ${colors.border};
`;

const Warn = webStyled.div`
  font-weight: 600;
  color: red;
`;

const TABS = [
  {
    key: 'note',
    label: 'Create Note',
    icon: faNote,
    submitLabel: 'Create Note',
    enabled: conversation => conversation,
  },
  {
    key: 'call',
    label: 'Make Call',
    icon: faPhone,
    enabled: conversation => conversation?.consumer.mobileNumber,
  },
  {
    key: 'email',
    label: 'Send Email',
    icon: faEnvelope,
    submitLabel: 'Send Email',
    enabled: conversation => conversation?.consumer.email,
  },
  {
    key: 'textonix',
    label: 'Send Message',
    icon: faMessageLines,
    submitLabel: 'Send Text',
    enabled: conversation => true,
  },
];

const MINIMAL_TABS = TABS.filter(tab => ['textonix', 'note'].includes(tab.key));

const CHANNEL_LABELS = {
  twilio: 'Twilio (text messages)',
  messenger: 'Facebook Messenger',
  whatsapp: 'Whatsapp',
  chat: 'Chat Widget',
  fbcomment: 'Facebook Comment',
};

const FormContainer = ({ onEnter, children, style }) => {
  useEffect(() => {
    const handleKeyUp = (e) => {
      if (13 === e.keyCode && onEnter) {
        onEnter();
      }
    }

    window.document.addEventListener('keyup', handleKeyUp);

    return () => {
      window.document.removeEventListener('keyup', handleKeyUp);
    }
  }, [onEnter]);

  return (
    <Container>{children}</Container>
  )
};

const Compose = ({ conversation, refetch, minimal, key }) => {
  const [isImportant, setIsImportant] = useState(false);
  const conversationContext = useContext(MessageInputContext);
  const {
    body, setBody, currentConversationId, setCurrentConversationId
  } = conversationContext;

  const [createMessage] = useMutation(CREATE_MESSAGE, {
    onCompleted: () => {
      setBody('');
      if (refetch) { refetch() };
    }
  });

  const [recordUserActivity] = useMutation(RECORD_USER_ACTIVITY);

  const updateBody = value => {
    recordUserActivity({
      variables: {
        conversationId: currentConversationId,
        activity: 'typing',
        text: value
      }
    });

    setBody(value);
  };

  const handleSubmit = () => {
    const variables = {
      body,
      conversationId: currentConversationId,
      source: 'Buyer',
    };

    if (isImportant) {
      variables.flags = 'important';
    }

    if(currentConversationId === conversation.id &&
      (body && body.length && body !== '\n')
    ) {
      createMessage({ variables });
    }
  };

  return (
    <FormContainer onEnter={handleSubmit}>
      {
        <Form>
          <Input
            type="text"
            value={currentConversationId === conversation.id ? body : ''}
            onChangeText={updateBody}
            multiline={true}
            numberOfLines={2}
            key={key}
            name={`conversation-${conversation.id}`}
            onFocus={() => {
              if(currentConversationId !== conversation.id) {
                setCurrentConversationId(conversation.id);
              }
            }}
          />

          <ButtonContainer>
            <SendButton onClick={handleSubmit}>
              <SendLabel>Send</SendLabel>
            </SendButton>
          </ButtonContainer>
        </Form>
      }
    </FormContainer>
  );
};

export default Compose;
